import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { renderHelpCenterMarkdown } from '../../utils/helpCenterHtml';
import { isHelpCenterCategory } from '../../utils/helpCenterCategories';
import HelpCenterLayout, { useHelpCenterCatalogContext } from './HelpCenterLayout';

const SLUG_PATTERN = /^[a-z0-9-]+$/;

const ArticleBackLink = ({ slug }: { slug?: string }) => {
  const { t } = useTranslation();
  const { articles } = useHelpCenterCatalogContext();
  const article = articles.find((entry) => entry.slug === slug);
  const backTo =
    article?.category && isHelpCenterCategory(article.category)
      ? `/help-center/category/${article.category}`
      : '/help-center';

  return (
    <Link
      className="text-slate-700 underline underline-offset-2 hover:text-slate-900"
      to={backTo}
    >
      {t('helpCenter.back')}
    </Link>
  );
};

const HelpCenterArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [html, setHtml] = useState('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let isMounted = true;

    if (!slug || !SLUG_PATTERN.test(slug)) {
      console.error('Invalid Help Center article slug', { slug });
      setStatus('error');
      return undefined;
    }

    setStatus('loading');
    fetch(`/help-center/${slug}.md`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Help Center article HTTP ${response.status}`);
        }
        return response.text();
      })
      .then((markdown) => {
        if (isMounted) {
          setHtml(renderHelpCenterMarkdown(markdown));
          setStatus('ready');
        }
      })
      .catch((error) => {
        console.error('Failed to load Help Center article', { slug, error });
        if (isMounted) {
          setStatus('error');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [slug]);

  return (
    <HelpCenterLayout activeSlug={slug}>
      <>
        <ArticleBackLink slug={slug} />
        {status === 'loading' ? (
          <p className="mt-6 text-slate-700">{t('helpCenter.loading')}</p>
        ) : status === 'error' ? (
          <p className="mt-6 text-slate-700">{t('helpCenter.article_error')}</p>
        ) : (
          <article
            className="prose mt-6 max-w-none text-left text-slate-800 [&_a]:underline [&_a]:underline-offset-2 [&_h1]:mb-4 [&_h1]:text-4xl [&_h1]:font-extrabold [&_h1]:tracking-tight [&_h1]:text-slate-900 [&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_li]:mt-1 [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </>
    </HelpCenterLayout>
  );
};

export default HelpCenterArticlePage;
